import React from "react";
import { formatDuration } from "kontrolEdge/helpers/formatDuration";
import { EntryTone, TimelineSpec } from "./timelineSpec";

/**
 * An incident told in order, in the chat: what broke, every signal after it,
 * when it came back. Dots carry the health colours the drawings and the
 * Overview use, so red is down and green is back everywhere.
 */

const DOT: Record<EntryTone, string> = {
  down: "bg-semantic-fg-danger",
  up: "bg-semantic-fg-success",
  warning: "bg-semantic-fg-warning",
  info: "bg-semantic-fg-secondary",
};

function timeOf(iso: string, withDay: boolean): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const time = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  return withDay
    ? `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })} · ${time}`
    : time;
}

/** A timeline spanning more than one day shows the day on every entry. */
function spansDays(spec: TimelineSpec): boolean {
  const days = new Set(spec.entries.map((e) => new Date(e.at).toDateString()));
  return days.size > 1;
}

const ArchiTimeline: React.FC<{ spec: TimelineSpec }> = ({ spec }) => {
  const withDay = spansDays(spec);
  const active = spec.status === "active";
  return (
    <figure className="overflow-hidden rounded-2xl border border-semantic-border-primary motion-safe:animate-archi-rise">
      <figcaption className="flex flex-wrap items-center gap-2 border-b border-semantic-border-primary px-3 py-2">
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-semantic-text-primary">
          {spec.title}
        </span>
        {spec.duringMaintenance && (
          <span className="rounded-full bg-semantic-bg-secondary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-semantic-text-secondary">
            During maintenance
          </span>
        )}
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
            active
              ? "bg-semantic-bg-danger-secondary text-semantic-text-danger"
              : "bg-semantic-bg-success-secondary text-semantic-text-success"
          }`}
        >
          {active ? "Ongoing" : "Resolved"}
          {spec.durationSeconds !== null &&
            ` · ${formatDuration(spec.durationSeconds)}`}
        </span>
      </figcaption>
      {spec.entries.length === 0 ? (
        <p className="px-3 py-3 text-[13px] text-semantic-text-secondary">
          No events recorded for this incident.
        </p>
      ) : (
        <ol className="relative px-3 py-3">
          {/* The thread the dots hang on. */}
          <span
            aria-hidden
            className="absolute bottom-5 left-[19px] top-5 w-px bg-semantic-border-primary"
          />
          {spec.entries.map((entry, i) => (
            <li
              key={`${entry.at}-${i}`}
              className="relative flex gap-3 py-1.5 motion-safe:animate-archi-rise"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <span
                aria-hidden
                className={`relative z-10 mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ring-4 ring-semantic-bg-primary ${DOT[entry.tone]}`}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[13px] font-semibold text-semantic-text-primary">
                    {entry.title}
                  </span>
                  <time
                    dateTime={entry.at}
                    className="shrink-0 font-mono text-[11px] text-semantic-text-secondary"
                  >
                    {timeOf(entry.at, withDay)}
                  </time>
                </div>
                {(entry.detail || entry.service) && (
                  <p className="text-[12px] text-semantic-text-secondary">
                    {[entry.service, entry.detail].filter(Boolean).join(" · ")}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </figure>
  );
};

export default ArchiTimeline;
